import { EventClassification, EventTriggerSource } from '../models/DeviceEvent';
import { FlapState, MotionSensorState } from '../models/FrameMetadata';

type Translator = (key: string, options?: Record<string, unknown>) => string;

const formatEnumLabel = (label: string) => label.replace(/([a-z])([A-Z])/g, '$1 $2');

export type DurationUnit = 'seconds' | 'minutes' | 'hours';

const durationUnitMs: Record<DurationUnit, number> = {
    seconds: 1000,
    minutes: 60_000,
    hours: 3_600_000,
};

export const getTriggerSourceLabel = (t: Translator, source: EventTriggerSource) => {
    switch (source) {
        case EventTriggerSource.Manual:
            return t('door_policy.trigger_sources.manual');
        case EventTriggerSource.Remote:
            return t('door_policy.trigger_sources.remote');
        case EventTriggerSource.IndoorMotion:
            return t('door_policy.trigger_sources.indoor_motion');
        case EventTriggerSource.OutdoorMotion:
            return t('door_policy.trigger_sources.outdoor_motion');
        default: {
            const fallback = EventTriggerSource[source];
            return formatEnumLabel(typeof fallback === 'string' ? fallback : String(source));
        }
    }
};

export const getClassificationLabel = (t: Translator, classification: EventClassification) => {
    switch (classification) {
        case EventClassification.Unknown:
            return t('door_policy.classifications.unknown');
        case EventClassification.Clear:
            return t('door_policy.classifications.clear');
        case EventClassification.Suspicious:
            return t('door_policy.classifications.suspicious');
        case EventClassification.Contraband:
            return t('door_policy.classifications.contraband');
        case EventClassification.HumanActivity:
            return t('door_policy.classifications.human_activity');
        case EventClassification.RemoteUnlock:
            return t('door_policy.classifications.remote_unlock');
        default: {
            const fallback = EventClassification[classification];
            return formatEnumLabel(typeof fallback === 'string' ? fallback : String(classification));
        }
    }
};

export const getMotionSensorStateLabel = (t: Translator, state: MotionSensorState) => {
    switch (state) {
        case MotionSensorState.Unknown:
            return t('door_policy.motion_sensor_states.unknown');
        case MotionSensorState.None:
            return t('door_policy.motion_sensor_states.none');
        case MotionSensorState.Indoor:
            return t('door_policy.motion_sensor_states.indoor');
        case MotionSensorState.Outdoor:
            return t('door_policy.motion_sensor_states.outdoor');
        default: {
            const fallback = MotionSensorState[state];
            return formatEnumLabel(typeof fallback === 'string' ? fallback : String(state));
        }
    }
};

export const getFlapStateLabel = (t: Translator, state: FlapState) => {
    switch (state) {
        case FlapState.Unknown:
            return t('door_policy.flap_states.unknown');
        case FlapState.Closed:
            return t('door_policy.flap_states.closed');
        case FlapState.OpenOutward:
            return t('door_policy.flap_states.open_outward');
        case FlapState.OpenInward:
            return t('door_policy.flap_states.open_inward');
        case FlapState.Invalid:
            return t('door_policy.flap_states.invalid');
        default: {
            const fallback = FlapState[state];
            return formatEnumLabel(typeof fallback === 'string' ? fallback : String(state));
        }
    }
};

export const getDurationParts = (durationMs: number) => {
    const safeMs = Math.max(0, Math.round(durationMs));
    if (safeMs % durationUnitMs.hours === 0 && safeMs >= durationUnitMs.hours) {
        return { value: safeMs / durationUnitMs.hours, unit: 'hours' as const };
    }
    if (safeMs % durationUnitMs.minutes === 0 && safeMs >= durationUnitMs.minutes) {
        return { value: safeMs / durationUnitMs.minutes, unit: 'minutes' as const };
    }
    // Sub-minute durations keep 0.1s granularity so values like 1500ms read as "1.5 sec".
    return { value: Math.max(0.1, Math.round(safeMs / 100) / 10), unit: 'seconds' as const };
};

export const getDurationMs = (value: number, unit: DurationUnit) => Math.round(value * durationUnitMs[unit]);

export const formatDurationLabel = (
    t: Translator,
    durationMs: number,
    options?: { short?: boolean }
) => {
    const { value, unit } = getDurationParts(durationMs);
    const unitKey = options?.short ? `door_policy.duration_units.${unit}_short` : `door_policy.duration_units.${unit}`;
    return `${value} ${t(unitKey)}`;
};
